import { cbSemanticCssVars, typographyCss } from '@creditornot/cb-ingredients';
import { ModalContent, ModalHeader } from '@creditornot/cbt-modal';
import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import {
  BestOfWoltMetric,
  BestOfWoltMetricCondition,
  BestOfWoltMetricMeasurement,
  BestOfWoltMetricName,
  BestOfWoltMetricStatus,
} from 'apis/merchant-gateway/best-of-wolt/types';
import { WoltModal } from 'common/components';
import { MOBILE_BREAKPOINT, useIsMobile } from 'common/hooks/useIsMobile';
import { Typography } from 'common/typography';

import { MetricLabel, MetricLabelVariant } from './MetricLabel';

type MetricsProps = {
  metrics: BestOfWoltMetric[];
};

const MetricsContainer = styled.div`
  display: flex;
  gap: 16px;
  align-self: stretch;
  justify-content: space-between;
  flex-wrap: wrap;

  @media (width < ${MOBILE_BREAKPOINT}px) {
    gap: 2%;
  }
`;

const MetricContainer = styled.div`
  display: flex;
  padding: 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  flex: 1 0 0;
  border-radius: 8px;
  border: 1px solid ${cbSemanticCssVars.borderPrimary};

  @media (width < ${MOBILE_BREAKPOINT}px) {
    cursor: pointer;
    margin-block-end: 8px;
    align-items: center;
    width: 48%;
    flex: 0 0 48%;
  }
`;

const MetricInfoContainer = styled.div<{ centered: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ centered }) => (centered ? 'center' : 'flex-start')};
  gap: 4px;
`;

const MetricModalContent = styled(ModalContent)`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: 12px;
`;

const MetricDescription = styled(Typography.Small)`
  display: flex;
  height: 76px;
  flex-direction: column;
  justify-content: flex-end;
  color: ${cbSemanticCssVars.textSecondary};

  @media (width < ${MOBILE_BREAKPOINT}px) {
    height: auto;
  }
`;

const Heading3 = styled.span`
  ${typographyCss.Heading3()}
`;

const Heading6 = styled.span`
  ${typographyCss.Heading6()}
`;

const Body2 = styled.span`
  ${typographyCss.Body2()}
`;

const Body2Bold = styled.span`
  ${typographyCss.Body2({ weight: 'bold' })}
`;

const conditionTextMapping: Record<BestOfWoltMetricCondition, string> = {
  GREATER_THAN: 'best-of-wolt.metrics.goal.greater-than-metric',
  GREATER_THAN_OR_EQUAL: 'best-of-wolt.metrics.goal.greater-than-equal-metric',
  LESS_THAN: 'best-of-wolt.metrics.goal.less-than-metric',
  LESS_THAN_OR_EQUAL: 'best-of-wolt.metrics.goal.less-than-equal-metric',
  EQUAL: 'best-of-wolt.metrics.goal.equal-metric',
};

const Metric = ({ metric }: { metric: BestOfWoltMetric }) => {
  const { formatMessage, formatNumber } = useIntl();
  const formatMetricValue = useCallback(
    (value: number, measurement: BestOfWoltMetricMeasurement) => {
      if (measurement === 'PERCENTAGE') {
        return formatNumber(value, { style: 'percent', signDisplay: 'never', maximumFractionDigits: 2 });
      } else {
        return formatNumber(value, { maximumFractionDigits: 2 });
      }
    },
    [formatNumber],
  );

  const formattedTargetValue = formatMetricValue(metric.goal.target, metric.goal.measurement);

  const labelVariantMapping: Record<BestOfWoltMetricStatus, MetricLabelVariant> = {
    MET: 'success',
    BEHIND: 'alert',
    ON_TRACK: 'success',
    MISSED: 'error',
    NOT_MET: 'error',
  };

  const labelTextMapping: Record<BestOfWoltMetricStatus, string> = {
    MET: formatMessage({ id: 'best-of-wolt.metrics.status.met' }),
    BEHIND: formatMessage({ id: 'best-of-wolt.metrics.status.behind' }),
    ON_TRACK: formatMessage({ id: 'best-of-wolt.metrics.status.on-track' }),
    NOT_MET: formatMessage({ id: 'best-of-wolt.metrics.status.not-met' }),
    MISSED: formatMessage({ id: 'best-of-wolt.metrics.status.missed' }),
  };

  const metricTextMapping: Record<BestOfWoltMetricName, string> = {
    ORDERS: formatMessage({ id: 'best-of-wolt.metrics.orders.title' }),
    FOOD_RATING: formatMessage({ id: 'best-of-wolt.metrics.food-rating.title' }),
    ONLINE_TIME: formatMessage({ id: 'best-of-wolt.metrics.online-time.title' }),
    REJECTIONS: formatMessage({ id: 'best-of-wolt.metrics.rejections.title' }),
    MI_ITEMS: formatMessage({ id: 'best-of-wolt.metrics.mi-items.title' }),
  };

  const metricDescriptionMapping: Record<BestOfWoltMetricName, string> = {
    ORDERS: formatMessage({ id: 'best-of-wolt.metrics.orders.description' }, { target: formattedTargetValue }),
    FOOD_RATING: formatMessage(
      { id: 'best-of-wolt.metrics.food-rating.description' },
      { target: formattedTargetValue },
    ),
    ONLINE_TIME: formatMessage(
      { id: 'best-of-wolt.metrics.online-time.description' },
      { target: formattedTargetValue },
    ),
    REJECTIONS: formatMessage({ id: 'best-of-wolt.metrics.rejections.description' }, { target: formattedTargetValue }),
    MI_ITEMS: formatMessage({ id: 'best-of-wolt.metrics.mi-items.description' }, { target: formattedTargetValue }),
  };

  const isMobile = useIsMobile();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const conditionString = useMemo(() => {
    return conditionTextMapping[metric.goal.condition]
      ? formatMessage({ id: conditionTextMapping[metric.goal.condition] }, { target: formattedTargetValue })
      : formatMessage({ id: 'best-of-wolt.metrics.goal.undetermined' });
  }, [formatMessage, metric, formattedTargetValue]);

  return (
    <>
      <MetricContainer key={metric.goal.name} onClick={isMobile ? () => setIsModalOpen(true) : undefined}>
        <MetricLabel text={labelTextMapping[metric.status]} variant={labelVariantMapping[metric.status]} />
        <MetricInfoContainer centered={isMobile}>
          <Body2Bold>{metricTextMapping[metric.goal.name]}</Body2Bold>
          <Heading3>{formatMetricValue(metric.value, metric.goal.measurement)}</Heading3>
          <Body2>
            <FormattedMessage
              id="best-of-wolt.metrics.goal.title"
              values={{
                suffix: conditionString,
              }}
            />
          </Body2>
        </MetricInfoContainer>
        {!isMobile && <MetricDescription>{metricDescriptionMapping[metric.goal.name]}</MetricDescription>}
      </MetricContainer>
      <WoltModal isOpen={isModalOpen} onRequestClose={() => setIsModalOpen(false)} position="bottom">
        <ModalHeader onCloseClick={() => setIsModalOpen(false)}>
          <Heading6>
            <FormattedMessage
              id="best-of-wolt.metrics.goal.title"
              values={{
                suffix: metricTextMapping[metric.goal.name],
              }}
            />
          </Heading6>
        </ModalHeader>
        <MetricModalContent>
          <MetricLabel text={labelTextMapping[metric.status]} variant={labelVariantMapping[metric.status]} />
          <MetricInfoContainer centered={false}>
            <Heading3>{formatMetricValue(metric.value, metric.goal.measurement)}</Heading3>
            <Body2>
              <FormattedMessage
                id="best-of-wolt.metrics.goal.title"
                values={{
                  suffix: conditionString,
                }}
              />
            </Body2>
          </MetricInfoContainer>
          <MetricDescription>{metricDescriptionMapping[metric.goal.name]}</MetricDescription>
        </MetricModalContent>
      </WoltModal>
    </>
  );
};

export const Metrics = ({ metrics }: MetricsProps) => {
  return (
    <MetricsContainer>
      {metrics.map(metric => (
        <Metric metric={metric} />
      ))}
    </MetricsContainer>
  );
};
